import React from 'react';
import { Flame, Instagram, Play, Eye, Youtube, TrendingUp, Heart, MessageCircle, Send, Bookmark } from 'lucide-react';
import { SOCIAL } from '../mock';

const REEL_URL = 'https://www.instagram.com/reel/DbOcawpo_4R/';

export default function ViralReel() {

  return (
    <section id="viral-reel" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-fuchsia-700/10 blur-[130px]" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-violet-300/70 uppercase mb-6">
          <span className="h-px w-10 bg-violet-400/50" />
          <span>Trending Now · Straight From The Feed</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] mb-8">
              <span className="text-white">WENT </span>
              <span className="text-gradient-purple italic">VIRAL</span>
            </h2>
            <p className="text-white/60 leading-relaxed mb-8 max-w-lg flex items-start gap-2">
              <Flame className="w-5 h-5 text-violet-300 mt-1 shrink-0" />
              <span>This reel took off across Instagram — <span className="text-white">358K+ views and still climbing</span> — the mashup that had everyone asking "what track is this?" Press play and hear it live, straight from the source.</span>
            </p>

            {/* Live stats row */}
            <div className="grid grid-cols-2 gap-4 max-w-lg mb-8">
              <div className="p-4 rounded border border-violet-500/20 bg-violet-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-violet-300" />
                  <span className="font-mono text-[10px] tracking-[0.2em] text-violet-300/70 uppercase">Reel Views</span>
                </div>
                <div className="font-display text-3xl text-white">358K+</div>
                <div className="flex items-center gap-1 mt-1 text-[11px] text-emerald-400/90">
                  <TrendingUp className="w-3 h-3" /> Still going
                </div>
              </div>
              <div className="p-4 rounded border border-violet-500/20 bg-violet-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <Youtube className="w-4 h-4 text-violet-300" />
                  <span className="font-mono text-[10px] tracking-[0.2em] text-violet-300/70 uppercase">YouTube Growth</span>
                </div>
                <div className="font-display text-3xl text-white">+550</div>
                <div className="text-[11px] text-white/45 mt-1">New subscribers off this wave</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href={REEL_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-violet-300 hover:text-violet-200 uppercase"
              >
                <Instagram className="w-4 h-4" /> Watch on Instagram
              </a>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-violet-200/80 hover:text-violet-100 uppercase border border-violet-400/30 rounded-full px-5 py-2.5"
              >
                Follow @dj_ykas
              </a>
              <a
                href={SOCIAL.youtube}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-violet-200/80 hover:text-violet-100 uppercase border border-violet-400/30 rounded-full px-5 py-2.5"
              >
                Subscribe on YouTube
              </a>
            </div>
          </div>

          {/* Branded reel preview card — Instagram-style UI, dark mode, always on-theme */}
          <div className="flex justify-center">
            <div className="w-full max-w-[400px] rounded-xl border border-violet-500/25 bg-[#0d0716] overflow-hidden shadow-[0_0_40px_-10px_rgba(167,139,250,0.25)]">

              {/* Post header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src="/reel/reel-avatar.png"
                    alt="dj_ykas"
                    className="w-9 h-9 rounded-full shrink-0 object-cover ring-1 ring-violet-400/30"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">dj_ykas</div>
                    <div className="text-[11px] text-white/40 truncate">Chupke Se · DJ YKAS Edit</div>
                  </div>
                </div>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 font-mono text-[10px] tracking-[0.15em] uppercase text-white bg-violet-500 hover:bg-violet-400 rounded-md px-3 py-1.5 transition-colors"
                >
                  View Profile
                </a>
              </div>

              {/* Media area — real thumbnail, dark overlay for legibility */}
              <a
                href={REEL_URL}
                target="_blank"
                rel="noreferrer"
                className="group relative block overflow-hidden bg-black"
                style={{ aspectRatio: '797 / 444' }}
              >
                <img
                  src="/reel/reel-thumb.png"
                  alt="Chupke Se - DJ YKAS Edit"
                  className="absolute inset-0 w-full h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />

                <div className="relative h-full flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full border border-violet-300/50 bg-black/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-violet-500/30 group-hover:scale-105 transition-all">
                    <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
                  </div>
                </div>
              </a>

              {/* View more link */}
              <a
                href={REEL_URL}
                target="_blank"
                rel="noreferrer"
                className="block px-4 pt-3 text-sm text-violet-300 hover:text-violet-200"
              >
                View more on Instagram
              </a>

              {/* Action row */}
              <div className="flex items-center justify-between px-4 pt-3">
                <div className="flex items-center gap-4">
                  <Heart className="w-5 h-5 text-white/80" />
                  <MessageCircle className="w-5 h-5 text-white/80" />
                  <Send className="w-5 h-5 text-white/80" />
                </div>
                <Bookmark className="w-5 h-5 text-white/80" />
              </div>

              {/* Likes */}
              <div className="px-4 pt-2 text-sm font-semibold text-white">22,225 likes</div>

              {/* Comment box */}
              <div className="flex items-center justify-between px-4 py-3 mt-1 border-t border-white/5">
                <span className="text-sm text-white/35">Add a comment…</span>
                <Instagram className="w-4 h-4 text-white/40" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
